"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { type RequestType } from "@/types/busrequest";
import { AddressWithLocation } from "@/types/location";

export type BusrequestMapFilters = {
  typeFilter: RequestType[] | null;
  startDateRangeFilter: [Date | null, Date | null];
  endDateRangeFilter: [Date | null, Date | null];
  endLocationFilter: {
    location: AddressWithLocation | null;
    radiusKm: number | null;
  };
  startLocationFilter: {
    location: AddressWithLocation | null;
    radiusKm: number | null;
  };
};

type BusrequestMapContextType = BusrequestMapFilters & {
  setTypeFilter: (type: RequestType[] | null) => void;
  setStartDateRangeFilter: (
    startDate: Date | null,
    endDate: Date | null,
  ) => void;
  setStartDateStart: (startDate: Date | null) => void;
  setStartDateEnd: (endDate: Date | null) => void;
  setEndDateStart: (startDate: Date | null) => void;
  setEndDateEnd: (endDate: Date | null) => void;
  setEndDateRangeFilter: (startDate: Date | null, endDate: Date | null) => void;
  setStartLocation: (address: AddressWithLocation | null) => void;
  setStartRadius: (radius: number | null) => void;
  setStartLocationFilter: (
    address: AddressWithLocation | null,
    radiusKm?: number,
  ) => void;
  setEndLocation: (address: AddressWithLocation | null) => void;
  setEndRadius: (radius: number | null) => void;
  setEndLocationFilter: (
    address: AddressWithLocation | null,
    radiusKm?: number,
  ) => void;
  amountFilterApplied: number;
};

export const BusrequestMapContext =
  createContext<BusrequestMapContextType | null>(null);

export const useBusrequestMapContext = () => {
  const context = useContext(BusrequestMapContext);
  if (!context) {
    throw new Error(
      "useBusrequestMap must be used within a BusrequestMapProvider",
    );
  }
  return context;
};

export const BusrequestMapProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [filters, setFilters] = useState<BusrequestMapFilters>({
    typeFilter: null,
    startDateRangeFilter: [null, null],
    endDateRangeFilter: [null, null],
    startLocationFilter: { location: null, radiusKm: null },
    endLocationFilter: { location: null, radiusKm: null },
  });

  const setTypeFilter = useCallback((types: RequestType[] | null) => {
    setFilters((prev) => ({ ...prev, typeFilter: types }));
  }, []);

  const setStartDateRangeFilter = useCallback(
    (startDate: Date | null, endDate: Date | null) => {
      setFilters((prev) => ({
        ...prev,
        startDateRangeFilter: [startDate, endDate],
      }));
    },
    [],
  );

  const setEndDateRangeFilter = useCallback(
    (startDate: Date | null, endDate: Date | null) => {
      setFilters((prev) => ({
        ...prev,
        endDateRangeFilter: [startDate, endDate],
      }));
    },
    [],
  );

  const setStartDateStart = useCallback((startDate: Date | null) => {
    setFilters((prev) => ({
      ...prev,
      startDateRangeFilter: [startDate, prev.startDateRangeFilter[1]],
    }));
  }, []);

  const setStartDateEnd = useCallback((endDate: Date | null) => {
    setFilters((prev) => ({
      ...prev,
      startDateRangeFilter: [prev.startDateRangeFilter[0], endDate],
    }));
  }, []);

  const setEndDateStart = useCallback((startDate: Date | null) => {
    setFilters((prev) => ({
      ...prev,
      endDateRangeFilter: [startDate, prev.endDateRangeFilter[1]],
    }));
  }, []);

  const setEndDateEnd = useCallback((endDate: Date | null) => {
    setFilters((prev) => ({
      ...prev,
      endDateRangeFilter: [prev.endDateRangeFilter[0], endDate],
    }));
  }, []);

  const setStartLocation = useCallback(
    (address: AddressWithLocation | null) => {
      setFilters((prev) => ({
        ...prev,
        startLocationFilter: { ...prev.startLocationFilter, location: address },
      }));
    },
    [],
  );

  const setStartRadius = useCallback((radiusKm: number | null) => {
    setFilters((prev) => ({
      ...prev,
      startLocationFilter: { ...prev.startLocationFilter, radiusKm },
    }));
  }, []);

  const setStartLocationFilter = useCallback(
    (address: AddressWithLocation | null, radiusKm?: number) => {
      setFilters((prev) => ({
        ...prev,
        startLocationFilter: {
          location: address,
          radiusKm: radiusKm ?? null,
        },
      }));
    },
    [],
  );

  const setEndLocation = useCallback((address: AddressWithLocation | null) => {
    setFilters((prev) => ({
      ...prev,
      endLocationFilter: { ...prev.endLocationFilter, location: address },
    }));
  }, []);

  const setEndRadius = useCallback((radiusKm: number | null) => {
    setFilters((prev) => ({
      ...prev,
      endLocationFilter: { ...prev.endLocationFilter, radiusKm },
    }));
  }, []);

  const setEndLocationFilter = useCallback(
    (address: AddressWithLocation | null, radiusKm?: number) => {
      setFilters((prev) => ({
        ...prev,
        endLocationFilter: {
          location: address,
          radiusKm: radiusKm ?? null,
        },
      }));
    },
    [],
  );

  const amountFilterApplied = useMemo(() => {
    let amount = 0;
    if (!!filters.startLocationFilter.location) amount++;
    if (!!filters.endLocationFilter.location) amount++;
    if (!!filters.startDateRangeFilter[0]) amount++;
    if (!!filters.startDateRangeFilter[1]) amount++;
    if (!!filters.endDateRangeFilter[0]) amount++;
    if (!!filters.endDateRangeFilter[1]) amount++;
    return amount;
  }, [
    filters.startLocationFilter,
    filters.endLocationFilter,
    filters.startDateRangeFilter,
    filters.endDateRangeFilter,
  ]);

  const value = useMemo(
    () =>
      ({
        ...filters,
        setTypeFilter,
        setStartDateRangeFilter,
        setEndDateRangeFilter,
        setStartDateStart,
        setStartDateEnd,
        setEndDateStart,
        setEndDateEnd,
        setStartLocation,
        setStartRadius,
        setStartLocationFilter,
        setEndLocation,
        setEndRadius,
        setEndLocationFilter,
        amountFilterApplied,
      }) as const,
    [
      filters,
      setTypeFilter,
      setStartDateRangeFilter,
      setEndDateRangeFilter,
      setStartDateStart,
      setStartDateEnd,
      setEndDateStart,
      setEndDateEnd,
      setStartLocation,
      setStartRadius,
      setStartLocationFilter,
      setEndLocation,
      setEndRadius,
      setEndLocationFilter,
      amountFilterApplied,
    ],
  );

  return (
    <BusrequestMapContext.Provider value={value}>
      {children}
    </BusrequestMapContext.Provider>
  );
};
